import React, { useState, useEffect } from 'react';
import { doc, getDoc, setDoc, collection, query, where, getDocs, addDoc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import { useAuth } from '../../context/AuthContext';
import { isFarm, canAccessFarmDashboard } from '../../utils/roleManager';
import { useTranslation } from 'react-i18next';
import { 
  createFarmInfoModel, 
  createGreenhouseModel, 
  createGrowingMethodModel, 
  createStrainModel, 
  createGrowingCycleModel 
} from '../../data/farmModels';

const FarmDashboard = () => {
  const { t } = useTranslation();
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview'); // overview, info, greenhouses, methods, strains, cycles
  
  // Data states
  const [farmInfo, setFarmInfo] = useState(null);
  const [greenhouses, setGreenhouses] = useState([]);
  const [growingMethods, setGrowingMethods] = useState([]);
  const [strains, setStrains] = useState([]);
  const [growingCycles, setGrowingCycles] = useState([]);
  
  // Form states
  const [showFarmInfoForm, setShowFarmInfoForm] = useState(false);
  const [showGreenhouseForm, setShowGreenhouseForm] = useState(false);
  const [showMethodForm, setShowMethodForm] = useState(false);
  const [showStrainForm, setShowStrainForm] = useState(false);
  const [showCycleForm, setShowCycleForm] = useState(false);
  
  const [farmInfoForm, setFarmInfoForm] = useState({
    farmName: '',
    description: '',
    knowledge: '',
    experience: '',
    yearsOfExperience: 0
  });
  
  const [greenhouseForm, setGreenhouseForm] = useState({
    name: '',
    description: '',
    area: '',
    type: 'indoor',
    capacity: ''
  });
  
  const [methodForm, setMethodForm] = useState({
    name: '',
    description: '',
    type: 'soil'
  });
  
  const [strainForm, setStrainForm] = useState({
    name: '',
    description: '',
    type: 'Hybrid',
    thcLevel: '',
    cbdLevel: ''
  });
  
  const [cycleForm, setCycleForm] = useState({
    strainId: '',
    strainName: '',
    greenhouseId: '',
    quantity: '',
    startDate: '',
    expectedHarvestDate: '',
    growingMethodId: ''
  });

  useEffect(() => {
    if (currentUser) {
      // ตรวจสอบว่าเป็น Farm หรือไม่
      if (!isFarm(currentUser)) {
        return;
      }
      fetchData();
    }
  }, [currentUser]);

  const fetchData = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      // ดึง Farm Info
      const farmInfoRef = doc(db, 'farmInfo', currentUser.uid);
      const farmInfoSnap = await getDoc(farmInfoRef);
      if (farmInfoSnap.exists()) {
        setFarmInfo({ id: farmInfoSnap.id, ...farmInfoSnap.data() });
        setFarmInfoForm({
          farmName: farmInfoSnap.data().farmName || '',
          description: farmInfoSnap.data().description || '',
          knowledge: farmInfoSnap.data().knowledge || '',
          experience: farmInfoSnap.data().experience || '',
          yearsOfExperience: farmInfoSnap.data().yearsOfExperience || 0
        });
      }
      
      // ดึง Greenhouses
      const greenhousesQuery = query(
        collection(db, 'greenhouses'),
        where('farmId', '==', currentUser.uid)
      );
      const greenhousesSnapshot = await getDocs(greenhousesQuery);
      setGreenhouses(greenhousesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })));
      
      // ดึง Growing Methods
      const methodsQuery = query(
        collection(db, 'growingMethods'),
        where('farmId', '==', currentUser.uid)
      );
      const methodsSnapshot = await getDocs(methodsQuery);
      setGrowingMethods(methodsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })));
      
      // ดึง Strains
      const strainsQuery = query(
        collection(db, 'strains'),
        where('farmId', '==', currentUser.uid)
      );
      const strainsSnapshot = await getDocs(strainsQuery);
      setStrains(strainsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })));
      
      // ดึง Growing Cycles
      const cyclesQuery = query(
        collection(db, 'growingCycles'),
        where('farmId', '==', currentUser.uid),
        where('status', 'in', ['planting', 'growing', 'flowering', 'harvesting'])
      );
      const cyclesSnapshot = await getDocs(cyclesQuery);
      setGrowingCycles(cyclesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })));
    } catch (error) {
      console.error('Error fetching farm data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveFarmInfo = async (e) => {
    e.preventDefault();
    if (!currentUser) return;
    
    try {
      const farmInfoData = createFarmInfoModel({
        farmId: currentUser.uid,
        ...farmInfoForm
      });
      
      const farmInfoRef = doc(db, 'farmInfo', currentUser.uid);
      await setDoc(farmInfoRef, farmInfoData, { merge: true });
      
      alert(t('farm.saveFarmInfoSuccess'));
      setShowFarmInfoForm(false);
      fetchData();
    } catch (error) {
      console.error('Error saving farm info:', error);
      alert(t('common.saveError') + error.message);
    }
  };

  const handleAddGreenhouse = async (e) => {
    e.preventDefault();
    if (!currentUser) return;
    
    try {
      const greenhouseData = createGreenhouseModel({
        farmId: currentUser.uid,
        name: greenhouseForm.name,
        description: greenhouseForm.description,
        area: parseFloat(greenhouseForm.area) || 0,
        type: greenhouseForm.type,
        capacity: parseInt(greenhouseForm.capacity) || 0
      });
      
      await addDoc(collection(db, 'greenhouses'), greenhouseData);
      alert(t('farm.addGreenhouseSuccess'));
      setShowGreenhouseForm(false);
      setGreenhouseForm({ name: '', description: '', area: '', type: 'indoor', capacity: '' });
      fetchData();
    } catch (error) {
      alert(t('common.saveError') + error.message);
    }
  };

  const handleAddMethod = async (e) => {
    e.preventDefault();
    if (!currentUser) return;
    
    try {
      const methodData = createGrowingMethodModel({
        farmId: currentUser.uid,
        name: methodForm.name,
        description: methodForm.description,
        type: methodForm.type
      });
      
      await addDoc(collection(db, 'growingMethods'), methodData);
      alert(t('farm.addMethodSuccess'));
      setShowMethodForm(false);
      setMethodForm({ name: '', description: '', type: 'soil' });
      fetchData();
    } catch (error) {
      alert(t('common.saveError') + error.message);
    }
  };

  const handleAddStrain = async (e) => {
    e.preventDefault();
    if (!currentUser) return;
    
    try {
      const strainData = createStrainModel({
        farmId: currentUser.uid,
        name: strainForm.name,
        description: strainForm.description,
        type: strainForm.type,
        thcLevel: strainForm.thcLevel ? parseFloat(strainForm.thcLevel) : null,
        cbdLevel: strainForm.cbdLevel ? parseFloat(strainForm.cbdLevel) : null
      });
      
      await addDoc(collection(db, 'strains'), strainData);
      alert(t('farm.addStrainSuccess'));
      setShowStrainForm(false);
      setStrainForm({ name: '', description: '', type: 'Hybrid', thcLevel: '', cbdLevel: '' });
      fetchData();
    } catch (error) {
      alert(t('common.saveError') + error.message);
    }
  };

  const handleAddCycle = async (e) => {
    e.preventDefault();
    if (!currentUser) return;
    
    try {
      const cycleData = createGrowingCycleModel({
        farmId: currentUser.uid,
        strainId: cycleForm.strainId,
        strainName: cycleForm.strainName,
        greenhouseId: cycleForm.greenhouseId || null,
        quantity: parseInt(cycleForm.quantity) || 0,
        growingMethodId: cycleForm.growingMethodId || null,
        startDate: cycleForm.startDate ? new Date(cycleForm.startDate) : new Date(),
        expectedHarvestDate: cycleForm.expectedHarvestDate ? new Date(cycleForm.expectedHarvestDate) : null
      });
      
      await addDoc(collection(db, 'growingCycles'), cycleData);
      alert(t('farm.saveCycleSuccess'));
      setShowCycleForm(false);
      setCycleForm({ strainId: '', strainName: '', greenhouseId: '', quantity: '', startDate: '', expectedHarvestDate: '', growingMethodId: '' });
      fetchData();
    } catch (error) {
      alert(t('common.saveError') + error.message);
    }
  };

  // ตรวจสอบสิทธิ์
  if (!currentUser || !isFarm(currentUser)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 text-lg">{t('farm.noAccess')}</p>
          <p className="text-gray-600 mt-2">{t('farm.farmOnly')}</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="container mx-auto px-4">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">🌾 {t('farm.dashboard')}</h1>
          <p className="text-gray-600">{t('farm.dashboardDescription')}</p>
        </div>

        {/* Tabs */}
        <div className="bg-white rounded-lg shadow-md mb-6">
          <div className="flex overflow-x-auto border-b">
            {[
              { id: 'overview', label: t('farm.overview'), icon: '📊' },
              { id: 'info', label: t('farm.farmInfo'), icon: 'ℹ️' },
              { id: 'greenhouses', label: t('farm.greenhouses'), icon: '🏠' },
              { id: 'methods', label: t('farm.growingMethods'), icon: '🌱' },
              { id: 'strains', label: t('farm.strains'), icon: '🌿' },
              { id: 'cycles', label: t('farm.growingCycles'), icon: '📅' }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-3 font-medium whitespace-nowrap border-b-2 transition ${
                  activeTab === tab.id
                    ? 'border-green-500 text-green-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="text-3xl mb-2">🏠</div>
              <div className="text-2xl font-bold text-gray-800">{greenhouses.length}</div>
              <div className="text-gray-600">{t('farm.greenhouses')}</div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="text-3xl mb-2">🌱</div>
              <div className="text-2xl font-bold text-gray-800">{growingMethods.length}</div>
              <div className="text-gray-600">{t('farm.growingMethods')}</div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="text-3xl mb-2">🌿</div>
              <div className="text-2xl font-bold text-gray-800">{strains.length}</div>
              <div className="text-gray-600">{t('farm.strains')}</div>
            </div>
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="text-3xl mb-2">📅</div>
              <div className="text-2xl font-bold text-gray-800">{growingCycles.length}</div>
              <div className="text-gray-600">{t('farm.activeCycles')}</div>
            </div>
          </div>
        )}

        {/* Farm Info Tab */}
        {activeTab === 'info' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-800">{t('farm.farmInfo')}</h2>
              <button
                onClick={() => setShowFarmInfoForm(!showFarmInfoForm)}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
              >
                {showFarmInfoForm ? t('common.cancel') : farmInfo ? t('common.edit') : t('farm.addInfo')}
              </button>
            </div>

            {showFarmInfoForm ? (
              <form onSubmit={handleSaveFarmInfo} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('farm.farmName')}</label>
                  <input
                    type="text"
                    value={farmInfoForm.farmName}
                    onChange={(e) => setFarmInfoForm({ ...farmInfoForm, farmName: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('marketplace.description')}</label>
                  <textarea
                    value={farmInfoForm.description}
                    onChange={(e) => setFarmInfoForm({ ...farmInfoForm, description: e.target.value })}
                    rows="4"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('farm.knowledge')}</label>
                  <textarea
                    value={farmInfoForm.knowledge}
                    onChange={(e) => setFarmInfoForm({ ...farmInfoForm, knowledge: e.target.value })}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('farm.experience')}</label>
                  <textarea
                    value={farmInfoForm.experience}
                    onChange={(e) => setFarmInfoForm({ ...farmInfoForm, experience: e.target.value })}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('farm.yearsOfExperience')}</label>
                  <input
                    type="number"
                    value={farmInfoForm.yearsOfExperience}
                    onChange={(e) => setFarmInfoForm({ ...farmInfoForm, yearsOfExperience: parseInt(e.target.value) || 0 })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    min="0"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full py-3 bg-green-500 text-white rounded-lg hover:bg-green-600"
                >
                  {t('common.save')}
                </button>
              </form>
            ) : farmInfo ? (
              <div className="space-y-4">
                <div>
                  <h3 className="font-semibold text-gray-700">{t('farm.farmName')}:</h3>
                  <p className="text-gray-800">{farmInfo.farmName}</p>
                </div>
                {farmInfo.description && (
                  <div>
                    <h3 className="font-semibold text-gray-700">{t('marketplace.description')}:</h3>
                    <p className="text-gray-800">{farmInfo.description}</p>
                  </div>
                )}
                {farmInfo.knowledge && (
                  <div>
                    <h3 className="font-semibold text-gray-700">{t('farm.knowledge')}:</h3>
                    <p className="text-gray-800">{farmInfo.knowledge}</p>
                  </div>
                )}
                {farmInfo.experience && (
                  <div>
                    <h3 className="font-semibold text-gray-700">{t('farm.experience')}:</h3>
                    <p className="text-gray-800">{farmInfo.experience}</p>
                  </div>
                )}
                {farmInfo.yearsOfExperience > 0 && (
                  <div>
                    <h3 className="font-semibold text-gray-700">{t('farm.yearsOfExperience')}:</h3>
                    <p className="text-gray-800">{farmInfo.yearsOfExperience} {t('farm.years')}</p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-gray-600">{t('farm.noFarmInfo')}</p>
            )}
          </div>
        )}

        {/* Greenhouses Tab */}
        {activeTab === 'greenhouses' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-800">{t('farm.greenhouses')}</h2>
              <button
                onClick={() => setShowGreenhouseForm(!showGreenhouseForm)}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
              >
                {showGreenhouseForm ? t('common.cancel') : `+ ${t('farm.addGreenhouse')}`}
              </button>
            </div>

            {showGreenhouseForm && (
              <form onSubmit={handleAddGreenhouse} className="mb-6 p-4 bg-gray-50 rounded-lg space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('farm.greenhouseName')} *</label>
                  <input
                    type="text"
                    value={greenhouseForm.name}
                    onChange={(e) => setGreenhouseForm({ ...greenhouseForm, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('marketplace.description')}</label>
                  <textarea
                    value={greenhouseForm.description}
                    onChange={(e) => setGreenhouseForm({ ...greenhouseForm, description: e.target.value })}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('farm.area')}</label>
                    <input
                      type="number"
                      value={greenhouseForm.area}
                      onChange={(e) => setGreenhouseForm({ ...greenhouseForm, area: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      min="0"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('farm.capacity')}</label>
                    <input
                      type="number"
                      value={greenhouseForm.capacity}
                      onChange={(e) => setGreenhouseForm({ ...greenhouseForm, capacity: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      min="0"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('farm.type')}</label>
                  <select
                    value={greenhouseForm.type}
                    onChange={(e) => setGreenhouseForm({ ...greenhouseForm, type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="indoor">Indoor</option>
                    <option value="outdoor">Outdoor</option>
                    <option value="greenhouse">Greenhouse</option>
                    <option value="hybrid">Hybrid</option>
                  </select>
                </div>
                <button
                  type="submit"
                  className="w-full py-3 bg-green-500 text-white rounded-lg hover:bg-green-600"
                >
                  {t('farm.addGreenhouse')}
                </button>
              </form>
            )}

            <div className="space-y-4">
              {greenhouses.length > 0 ? (
                greenhouses.map(greenhouse => (
                  <div key={greenhouse.id} className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-800">{greenhouse.name}</h3>
                    {greenhouse.description && <p className="text-gray-600 text-sm mt-1">{greenhouse.description}</p>}
                    <div className="mt-2 text-sm text-gray-600">
                      <span>{t('farm.area')}: {greenhouse.area} {t('farm.sqm')}</span>
                      <span className="ml-4">{t('farm.capacity')}: {greenhouse.capacity} {t('farm.plants')}</span>
                      <span className="ml-4">{t('farm.type')}: {greenhouse.type}</span>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-600">{t('farm.noGreenhouses')}</p>
              )}
            </div>
          </div>
        )}

        {/* Growing Methods Tab */}
        {activeTab === 'methods' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-800">{t('farm.growingMethods')}</h2>
              <button
                onClick={() => setShowMethodForm(!showMethodForm)}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
              >
                {showMethodForm ? t('common.cancel') : `+ ${t('farm.addMethod')}`}
              </button>
            </div>

            {showMethodForm && (
              <form onSubmit={handleAddMethod} className="mb-6 p-4 bg-gray-50 rounded-lg space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('farm.methodName')} *</label>
                  <input
                    type="text"
                    value={methodForm.name}
                    onChange={(e) => setMethodForm({ ...methodForm, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('marketplace.description')}</label>
                  <textarea
                    value={methodForm.description}
                    onChange={(e) => setMethodForm({ ...methodForm, description: e.target.value })}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('farm.type')}</label>
                  <select
                    value={methodForm.type}
                    onChange={(e) => setMethodForm({ ...methodForm, type: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="soil">Soil (ดิน)</option>
                    <option value="hydroponic">Hydroponic (ไฮโดรโปนิกส์)</option>
                    <option value="aeroponic">Aeroponic (แอโรโปนิกส์)</option>
                    <option value="organic">Organic (ออร์แกนิก)</option>
                    <option value="other">{t('farm.other')}</option>
                  </select>
                </div>
                <button
                  type="submit"
                  className="w-full py-3 bg-green-500 text-white rounded-lg hover:bg-green-600"
                >
                  {t('farm.addMethod')}
                </button>
              </form>
            )}

            <div className="space-y-4">
              {growingMethods.length > 0 ? (
                growingMethods.map(method => (
                  <div key={method.id} className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-800">{method.name}</h3>
                    {method.description && <p className="text-gray-600 text-sm mt-1">{method.description}</p>}
                    <p className="text-sm text-gray-600 mt-2">{t('farm.type')}: {method.type}</p>
                  </div>
                ))
              ) : (
                <p className="text-gray-600">{t('farm.noMethods')}</p>
              )}
            </div>
          </div>
        )}

        {/* Strains Tab */}
        {activeTab === 'strains' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-800">{t('farm.strains')}</h2>
              <button
                onClick={() => setShowStrainForm(!showStrainForm)}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
              >
                {showStrainForm ? t('common.cancel') : `+ ${t('farm.addStrain')}`}
              </button>
            </div>

            {showStrainForm && (
              <form onSubmit={handleAddStrain} className="mb-6 p-4 bg-gray-50 rounded-lg space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('farm.strainName')} *</label>
                  <input
                    type="text"
                    value={strainForm.name}
                    onChange={(e) => setStrainForm({ ...strainForm, name: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('marketplace.description')}</label>
                  <textarea
                    value={strainForm.description}
                    onChange={(e) => setStrainForm({ ...strainForm, description: e.target.value })}
                    rows="3"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('farm.type')}</label>
                    <select
                      value={strainForm.type}
                      onChange={(e) => setStrainForm({ ...strainForm, type: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="Sativa">Sativa</option>
                      <option value="Indica">Indica</option>
                      <option value="Hybrid">Hybrid</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('farm.thcLevel')}</label>
                    <input
                      type="number"
                      value={strainForm.thcLevel}
                      onChange={(e) => setStrainForm({ ...strainForm, thcLevel: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      min="0"
                      max="100"
                      step="0.1"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('farm.cbdLevel')}</label>
                  <input
                    type="number"
                    value={strainForm.cbdLevel}
                    onChange={(e) => setStrainForm({ ...strainForm, cbdLevel: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    min="0"
                    max="100"
                    step="0.1"
                  />
                </div>
                <button
                  type="submit"
                  className="w-full py-3 bg-green-500 text-white rounded-lg hover:bg-green-600"
                >
                  {t('farm.addStrain')}
                </button>
              </form>
            )}

            <div className="space-y-4">
              {strains.length > 0 ? (
                strains.map(strain => (
                  <div key={strain.id} className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-800">{strain.name}</h3>
                    {strain.description && <p className="text-gray-600 text-sm mt-1">{strain.description}</p>}
                    <div className="mt-2 text-sm text-gray-600">
                      <span>{t('farm.type')}: {strain.type}</span>
                      {strain.thcLevel && <span className="ml-4">THC: {strain.thcLevel}%</span>}
                      {strain.cbdLevel && <span className="ml-4">CBD: {strain.cbdLevel}%</span>}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-600">{t('farm.noStrains')}</p>
              )}
            </div>
          </div>
        )}

        {/* Growing Cycles Tab */}
        {activeTab === 'cycles' && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold text-gray-800">{t('farm.growingCycles')}</h2>
              <button
                onClick={() => setShowCycleForm(!showCycleForm)}
                className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600"
              >
                {showCycleForm ? t('common.cancel') : `+ ${t('farm.saveCycle')}`}
              </button>
            </div>

            {showCycleForm && (
              <form onSubmit={handleAddCycle} className="mb-6 p-4 bg-gray-50 rounded-lg space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('farm.strains')} *</label>
                  <select
                    value={cycleForm.strainId}
                    onChange={(e) => {
                      const selectedStrain = strains.find(s => s.id === e.target.value);
                      setCycleForm({
                        ...cycleForm,
                        strainId: e.target.value,
                        strainName: selectedStrain?.name || ''
                      });
                    }}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    required
                  >
                    <option value="">{t('farm.selectStrain')}</option>
                    {strains.map(strain => (
                      <option key={strain.id} value={strain.id}>{strain.name}</option>
                    ))}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('farm.quantity')} *</label>
                    <input
                      type="number"
                      value={cycleForm.quantity}
                      onChange={(e) => setCycleForm({ ...cycleForm, quantity: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      required
                      min="1"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('farm.greenhouses')}</label>
                    <select
                      value={cycleForm.greenhouseId}
                      onChange={(e) => setCycleForm({ ...cycleForm, greenhouseId: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="">{t('farm.notSpecified')}</option>
                      {greenhouses.map(gh => (
                        <option key={gh.id} value={gh.id}>{gh.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('farm.startDate')} *</label>
                    <input
                      type="date"
                      value={cycleForm.startDate}
                      onChange={(e) => setCycleForm({ ...cycleForm, startDate: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('farm.expectedHarvestDate')}</label>
                    <input
                      type="date"
                      value={cycleForm.expectedHarvestDate}
                      onChange={(e) => setCycleForm({ ...cycleForm, expectedHarvestDate: e.target.value })}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{t('farm.growingMethods')}</label>
                  <select
                    value={cycleForm.growingMethodId}
                    onChange={(e) => setCycleForm({ ...cycleForm, growingMethodId: e.target.value })}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                  >
                    <option value="">{t('farm.notSpecified')}</option>
                    {growingMethods.map(method => (
                      <option key={method.id} value={method.id}>{method.name}</option>
                    ))}
                  </select>
                </div>
                <button
                  type="submit"
                  className="w-full py-3 bg-green-500 text-white rounded-lg hover:bg-green-600"
                >
                  {t('farm.saveCycle')}
                </button>
              </form>
            )}

            <div className="space-y-4">
              {growingCycles.length > 0 ? (
                growingCycles.map(cycle => (
                  <div key={cycle.id} className="border border-gray-200 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-800">{cycle.strainName}</h3>
                    <div className="mt-2 text-sm text-gray-600 space-y-1">
                      <div>{t('farm.quantity')}: {cycle.quantity} {t('farm.plants')}</div>
                      <div>{t('farm.startPlanting')}: {cycle.startDate?.toDate?.().toLocaleDateString('th-TH') || 'N/A'}</div>
                      {cycle.expectedHarvestDate && (
                        <div>{t('farm.expectedHarvest')}: {cycle.expectedHarvestDate?.toDate?.().toLocaleDateString('th-TH') || 'N/A'}</div>
                      )}
                      <div>{t('orders.status')}: {cycle.status}</div>
                      {cycle.progress > 0 && (
                        <div>{t('farm.progress')}: {cycle.progress}%</div>
                      )}
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-600">{t('farm.noCycles')}</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FarmDashboard;

